import { Link, NavLink, Outlet } from "react-router-dom";
import {
  Map as MapIcon, LayoutDashboard, Hospital, Syringe, Users as UsersIcon,
  Star, BookOpen, Settings, ListChecks, Activity, ShieldCheck, FileSearch,
  LogOut, User as UserIcon,
} from "lucide-react";
import { isAdmin, useAuth } from "../lib/auth";

interface NavItem {
  to: string;
  label: string;
  icon: any;
  roles?: string[];
}

const PRIMARY: NavItem[] = [
  { to: "/map", label: "Карта", icon: MapIcon },
  { to: "/dashboard", label: "Дашборд", icon: LayoutDashboard },
  { to: "/facilities", label: "Лікарні", icon: Hospital },
  { to: "/interventions", label: "Інтервенції", icon: Syringe, roles: ["analyst", "admin", "super_admin", "viewer"] },
  { to: "/groups", label: "Групи", icon: UsersIcon, roles: ["analyst", "admin", "super_admin", "viewer"] },
  { to: "/presets", label: "Пресети", icon: Star, roles: ["analyst", "admin", "super_admin", "viewer"] },
  { to: "/methodology", label: "Методологія", icon: BookOpen },
];

const ADMIN: NavItem[] = [
  { to: "/admin/sync", label: "Sync", icon: Activity },
  { to: "/admin/uptime", label: "Аптайм", icon: ShieldCheck },
  { to: "/admin/data-quality", label: "Якість даних", icon: ListChecks },
  { to: "/admin/settings", label: "Налаштування", icon: Settings },
  { to: "/admin/users", label: "Користувачі", icon: UsersIcon },
  { to: "/admin/audit-log", label: "Аудит-лог", icon: FileSearch },
];

export function Layout() {
  const { user, logout } = useAuth();
  const role = user?.role;

  const visiblePrimary = PRIMARY.filter(
    (n) => !n.roles || n.roles.includes(role || "")
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--c-bg)" }}>
      <aside
        style={{
          width: 232,
          background: "linear-gradient(180deg, #1a365d 0%, #14304f 100%)",
          color: "white",
          display: "flex",
          flexDirection: "column",
          boxShadow: "inset -1px 0 0 rgba(255,255,255,0.05), 4px 0 24px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Link
          to="/map"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "20px 22px 18px",
            fontSize: 17,
            fontWeight: 700,
            color: "white",
            textDecoration: "none",
            borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
            letterSpacing: -0.3,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #4299e1, #2c5282)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              boxShadow: "0 2px 8px rgba(66, 153, 225, 0.4)",
            }}
          >
            ⊕
          </span>
          HRS
        </Link>
        <nav style={{ flex: 1, display: "flex", flexDirection: "column", padding: "12px 8px" }}>
          {visiblePrimary.map((n) => (
            <SidebarLink key={n.to} item={n} />
          ))}
          {isAdmin(role) && (
            <>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(160, 174, 192, 0.7)",
                  padding: "18px 14px 6px",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontWeight: 600,
                }}
              >
                Адмінка
              </div>
              {ADMIN.map((n) => (
                <SidebarLink key={n.to} item={n} />
              ))}
            </>
          )}
        </nav>
        {user && (
          <div
            style={{
              borderTop: "1px solid rgba(255, 255, 255, 0.06)",
              padding: "14px 18px",
              fontSize: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "rgba(66, 153, 225, 0.15)",
                  border: "1px solid rgba(66, 153, 225, 0.3)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <UserIcon size={14} />
              </div>
              <div style={{ overflow: "hidden", flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {user.full_name || user.email}
                </div>
                <div style={{ color: "rgba(160, 174, 192, 0.85)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {user.role.replace("_", " ")}
                </div>
              </div>
            </div>
            <button
              onClick={logout}
              style={{
                marginTop: 10,
                width: "100%",
                background: "rgba(255, 255, 255, 0.04)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: "rgba(203, 213, 224, 0.95)",
                padding: "6px 10px",
                borderRadius: 6,
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                transition: "all var(--t-fast)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(229, 62, 62, 0.15)";
                e.currentTarget.style.borderColor = "rgba(229, 62, 62, 0.3)";
                e.currentTarget.style.color = "#fc8181";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.color = "rgba(203, 213, 224, 0.95)";
              }}
            >
              <LogOut size={12} /> Вийти
            </button>
          </div>
        )}
      </aside>
      <main style={{ flex: 1, overflow: "auto" }}>
        <Outlet />
      </main>
    </div>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 14px",
        margin: "1px 0",
        color: isActive ? "white" : "rgba(203, 213, 224, 0.85)",
        background: isActive ? "rgba(66, 153, 225, 0.15)" : "transparent",
        textDecoration: "none",
        borderRadius: 6,
        fontSize: 13.5,
        fontWeight: isActive ? 600 : 500,
        transition: "all var(--t-fast)",
        position: "relative",
      })}
      onMouseEnter={(e) => {
        if (!e.currentTarget.style.background.includes("rgba(66")) {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
          e.currentTarget.style.color = "white";
        }
      }}
      onMouseLeave={(e) => {
        const isActive = e.currentTarget.getAttribute("aria-current") === "page";
        if (!isActive) {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = "rgba(203, 213, 224, 0.85)";
        }
      }}
    >
      <Icon size={15} />
      {item.label}
    </NavLink>
  );
}
